import { useState, useCallback, useRef, useMemo, useEffect } from "react";
import DOMPurify from 'dompurify';
import EmojiPicker from 'emoji-picker-react';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import { Network, Fullscreen, Minimize, FileText } from 'lucide-react';
import { FullScreen as FullScreenView, useFullScreenHandle } from 'react-full-screen';
import SnackbarManager from '@CommonFunctionalDisplay/GlobalSnackbar/SnackbarManager';
import { useReduxLeads } from '@Hooks/reduxLeadsHook/useReduxLeads';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import WhatsappBotPreview from '@CommonRepresentationalDisplay/WhatsappBotPreview';
import WhatsappFlowService from '@Helpers/URLHelpers/WhatsappFlowService';
import ErrorHandler from '@Root/ErrorHandler';
import "./AutoReplies.scss";

const CFG = {
    start: { label: "Start", bg: "#25D366", light: "#E8FBF0", border: "#A8EFC5", icon: "▶", desc: "Entry point" },
    text_msg: { label: "Text Message", bg: "#0A8F72", light: "#E6F7F5", border: "#9DDCD3", icon: "💬", desc: "Plain WhatsApp text" },
    interactive: { label: "Interactive Msg", bg: "#1D74F5", light: "#EBF3FF", border: "#93C4FB", icon: "🔘", desc: "Message with reply buttons" },
    wait_reply: { label: "Wait for Reply", bg: "#7C3AED", light: "#F3EDFD", border: "#C4AAEE", icon: "⌨️", desc: "Branch by user input" },
    leads: { label: "Convert to Lead", bg: "#7C3AED", light: "#F3EDFD", border: "#C4AAEE", icon: "⌨️", desc: "Convert Leads" },
    unsubscribe: { label: "Unsubscribe User", bg: "#DC2626", light: "#FEF2F2", border: "#FCA5A5", icon: "🚫", desc: "Unsubscribe" },
    end: { label: "End", bg: "#DC2626", light: "#FEF0F0", border: "#FABEBE", icon: "⏹", desc: "End conversation" },
};

const NODE_W = 268; const HDR_H = 44; const PAD = 12;
const BTN_ROW = 38; const RULE_ROW = 38;
const BTN_COLORS = ["#1D74F5", "#0A8F72", "#7C3AED", "#D97706", "#DC2626"];
const RULE_COLORS = ["#25D366", "#0A8F72", "#7C3AED", "#D97706", "#DC2626", "#1D74F5", "#475569"];

const HEADER_PLACEHOLDERS = {
    image: 'https://example.com/photo.jpg',
    video: 'https://example.com/video.mp4',
    document: 'https://example.com/file.pdf',
};

function nodeH(n) {
    if (n.type === "interactive") {
        const btns = n.data?.buttons?.length || 0;
        const hdrType = n.data?.headerType;
        const hasHdr = hdrType && hdrType !== "none";
        const hasFtr = !!n.data?.footer;
        let h = HDR_H + PAD;
        if (hasHdr) {
            if (hdrType === "image" && n.data?.headerLink) h += 78;
            else if (hdrType === "video" || hdrType === "document") h += 38;
            else h += 28;
        }
        h += Math.min((n.data?.body || "").length / 2.5 + 28, 72);
        if (hasFtr) h += 22;
        h += 8 + btns * BTN_ROW + PAD;
        return h;
    }
    if (n.type === "wait_reply") {
        const rules = n.data?.rules?.length || 0;
        return HDR_H + PAD + 36 + rules * RULE_ROW + 36 + PAD;
    }
    let h = 82;
    if (n.data?.text) h += Math.min(n.data.text.length / 2.2, 54);
    return h;
}

const inPos = n => ({ x: n.x + NODE_W / 2, y: n.y });
const outPos = n => ({ x: n.x + NODE_W / 2, y: n.y + nodeH(n) });

function btnHandlePos(n, i) {
    const hdrType = n.data?.headerType;
    const hasHdr = hdrType && hdrType !== "none";
    const hasFtr = !!n.data?.footer;
    let top = HDR_H + PAD;
    if (hasHdr) {
        if (hdrType === "image" && n.data?.headerLink) top += 78;
        else if (hdrType === "video" || hdrType === "document") top += 38;
        else top += 28;
    }
    top += Math.min((n.data?.body || "").length / 2.5 + 28, 72);
    if (hasFtr) top += 22;
    top += 8 + i * BTN_ROW + BTN_ROW / 2;
    return { x: n.x + NODE_W, y: n.y + top };
}

function ruleHandlePos(n, i) {
    return { x: n.x + NODE_W, y: n.y + HDR_H + PAD + 36 + i * RULE_ROW + RULE_ROW / 2 };
}

function fallbackHandlePos(n) {
    const rules = n.data?.rules?.length || 0;
    return { x: n.x + NODE_W, y: n.y + HDR_H + PAD + 36 + rules * RULE_ROW + 18 };
}

function bez(x1, y1, x2, y2) {
    const dx = Math.abs(x2 - x1), dy = Math.abs(y2 - y1);
    const cx2 = dx > dy ? dx * 0.42 : 0;
    const cy2 = Math.max(dy > dx ? dy * 0.42 : 52, 52);
    return `M${x1},${y1} C${x1 + cx2},${y1 + cy2} ${x2 - cx2},${y2 - cy2} ${x2},${y2}`;
}

const DEMO_NODES = [
    { id: "s1", type: "start", x: 360, y: 30, data: {} },
    {
        id: "n2", type: "wait_reply", x: 320, y: 160, data: {
            rules: [
                { keyword: "hi,hello,hii,hey", label: "Greeting", color: "#25D366" },
                { keyword: "price,cost,rate", label: "Pricing", color: "#1D74F5" },
                { keyword: "help,support", label: "Support", color: "#7C3AED" },
                { keyword: "stop, unsubscribe, not interested", label: "Unsubscribe", color: "#DC2626" },
            ]
        }
    },
    { x: 560, y: 439, id: "n3", data: {text: "" }, type: "unsubscribe" }
];

const DEMO_EDGES = [
    { id: "e1", source: "s1", target: "n2" },
    { id: "e2", kind: "rule", source: "n2", target: "n3", ruleIdx: 3}
];

function urlFilename(url, fallback) {
    try { return new URL(url).pathname.split('/').pop().slice(0, 24) || fallback; }
    catch { return fallback; }
}

export default function AutoReplies(props) {

    const [loading, setLoading] = useState(false);
    const [nodes, setNodes] = useState(DEMO_NODES);
    const [edges, setEdges] = useState(DEMO_EDGES);
    const [whatsappBotId, setWhatsappBotId] = useState(0);
    const [sel, setSel] = useState(null);
    const [drag, setDrag] = useState(null);
    const [conn, setConn] = useState(null);
    const [mouse, setMouse] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [tab, setTab] = useState("flow");
    const [showBodyEmoji, setShowBodyEmoji] = useState(false);
    const [showTextMsgEmoji, setShowTextMsgEmoji] = useState(false);
    const [keywordModal, setKeywordModal] = useState(null);
    const [draftKeywords, setDraftKeywords] = useState([]);
    const bodyPanelRef = useRef(null);
    const textMsgRef = useRef(null);
    const handle = useFullScreenHandle();
    const cvs = useRef(null);
    const { currentLeadsId, currentPhoneNumber, whatsappFlowProtection } = useReduxLeads();
    const { currentProjectId } = useReduxProject();
    const whatsappFlowService = new WhatsappFlowService();
    const errorHandler = new ErrorHandler();

    useEffect(() => { renderData(); }, []);

    const renderData = async () => {
        setLoading(true);
        if (!!props.whatsappFlow) {
            const wf = props.whatsappFlow;
            setWhatsappBotId(wf.id);
            const hasFlow = Object.keys(wf?.builderFlow || {}).length > 0;
            if (hasFlow) { setEdges(wf.builderFlow.edges); setNodes(wf.builderFlow.nodes); }
        }
        setLoading(false);
    };

    const selNode = useMemo(() => nodes.find(n => n.id === sel), [nodes, sel]);

    const handleSaveButton = async () => {
        try {
            const body = { ...saasFlowPayload, projectId: currentProjectId, phoneNumberId: currentPhoneNumber?.id, leadsConnectionId: currentLeadsId, id: whatsappBotId };
            const response = await whatsappFlowService.createWhatsappFlow(body);
            if (response.status < 400) { SnackbarManager.show('Saved', 'success'); props?.reloadData(); }
            else SnackbarManager.show(response.message, 'error');
        } catch (err) { errorHandler.handleError(err); }
    };

    const insertBodyFormat = useCallback((before, after = before) => {
        const ta = bodyPanelRef.current;
        if (!ta || !selNode) return;
        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        const body = selNode.data.body || '';
        const selected = body.substring(start, end);
        const newBody = body.substring(0, start) + before + selected + after + body.substring(end);
        upd(selNode.id, { body: newBody });
        setTimeout(() => {
            ta.focus();
            const cur = selected
                ? start + before.length
                : start + before.length + after.length;
            ta.setSelectionRange(
                selected ? start + before.length : cur,
                selected ? start + before.length + selected.length : cur
            );
        }, 0);
    }, [selNode]);

    const insertBodyVariable = useCallback(() => {
        const ta = bodyPanelRef.current;
        if (!ta || !selNode) return;
        const body = selNode.data.body || '';
        const nums = (body.match(/{{\d+}}/g) || []).map(v => parseInt(v.replace(/[{}]/g, '')));
        const next = nums.length ? Math.max(...nums) + 1 : 1;
        const start = ta.selectionStart;
        const token = `{{${next}}}`;
        upd(selNode.id, { body: body.substring(0, start) + token + body.substring(ta.selectionEnd) });
        setTimeout(() => { ta.focus(); ta.setSelectionRange(start + token.length, start + token.length); }, 0);
    }, [selNode]);

    const handleBodyEmojiClick = useCallback((emojiData) => {
        const ta = bodyPanelRef.current;
        if (!ta || !selNode) return;
        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        const body = selNode.data.body || '';
        const newBody = body.substring(0, start) + emojiData.emoji + body.substring(end);
        upd(selNode.id, { body: newBody });
        setShowBodyEmoji(false);
        setTimeout(() => { ta.focus(); ta.setSelectionRange(start + emojiData.emoji.length, start + emojiData.emoji.length); }, 0);
    }, [selNode]);

    const insertTextMsgFormat = useCallback((symbol) => {
        const ta = textMsgRef.current;
        if (!ta || !selNode) return;
        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        const text = selNode.data.text || '';
        const selected = text.substring(start, end);
        const newText = text.substring(0, start) + symbol + selected + symbol + text.substring(end);
        upd(selNode.id, { text: newText });
        setTimeout(() => {
            ta.focus();
            ta.setSelectionRange(
                selected ? start + symbol.length : start + symbol.length * 2,
                selected ? start + symbol.length + selected.length : start + symbol.length * 2
            );
        }, 0);
    }, [selNode]);

    const insertTextMsgVariable = useCallback(() => {
        const ta = textMsgRef.current;
        if (!ta || !selNode) return;
        const text = selNode.data.text || '';
        const nums = (text.match(/{{\d+}}/g) || []).map(v => parseInt(v.replace(/[{}]/g, '')));
        const next = nums.length ? Math.max(...nums) + 1 : 1;
        const start = ta.selectionStart;
        const token = `{{${next}}}`;
        upd(selNode.id, { text: text.substring(0, start) + token + text.substring(ta.selectionEnd) });
        setTimeout(() => { ta.focus(); ta.setSelectionRange(start + token.length, start + token.length); }, 0);
    }, [selNode]);

    const handleTextMsgEmojiClick = useCallback((emojiData) => {
        const ta = textMsgRef.current;
        if (!ta || !selNode) return;
        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        const text = selNode.data.text || '';
        upd(selNode.id, { text: text.substring(0, start) + emojiData.emoji + text.substring(end) });
        setShowTextMsgEmoji(false);
        setTimeout(() => { ta.focus(); ta.setSelectionRange(start + emojiData.emoji.length, start + emojiData.emoji.length); }, 0);
    }, [selNode]);

    const buildNodeWaJson = useCallback((n) => {
        if (n.type === "text_msg" || n.type === 'unsubscribe' || n.type === 'leads') return {
            messaging_product: "whatsapp", to: "{{recipient_phone}}",
            type: (n.type === 'text_msg' ? 'text' : n.type), text: { preview_url: false, body: n.data.text || "" }
        };
        if (n.type === "interactive") {
            const obj = {
                messaging_product: "whatsapp", to: "{{recipient_phone}}",
                type: "interactive",
                interactive: {
                    type: "button",
                    body: { text: n.data.body || "" },
                    action: { buttons: (n.data.buttons || []).map(b => ({ type: "reply", reply: { id: b.id, title: b.title } })) }
                }
            };
            if (n.data.headerType && n.data.headerType !== "none") {
                obj.interactive.header = n.data.headerType === "text"
                    ? { type: "text", text: n.data.header || "" }
                    : { type: n.data.headerType, [n.data.headerType]: { link: n.data.headerLink || "" } };
            }
            if (n.data.footer) obj.interactive.footer = { text: n.data.footer };
            return obj;
        }
        return null;
    }, []);

    const saasFlowPayload = useMemo(() => {
        const nodeMap = Object.fromEntries(nodes.map(n => [n.id, n]));
        const triggerMap = []; const triggerSet = new Set(); const waJsonCache = {};
        const getWaJson = (node) => {
            if (!node) return null;
            if (!waJsonCache[node.id]) { const built = buildNodeWaJson(node); if (!built) return null; waJsonCache[node.id] = built; }
            return waJsonCache[node.id];
        };
        const adjacency = {};
        edges.forEach(edge => { if (!adjacency[edge.source]) adjacency[edge.source] = []; adjacency[edge.source].push(edge); });
        const collectMessageChain = (startNode) => {
            const chain = []; let current = startNode; const visited = new Set();
            while (current) {
                if (visited.has(current.id)) break; visited.add(current.id);
                const waJson = getWaJson(current); if (waJson) chain.push(waJson);
                if (current.type === "wait_reply" || current.type === "interactive") break;
                const nextEdge = (adjacency[current.id] || []).find(e => e.kind !== "btn" && e.kind !== "rule" && e.kind !== "fallback");
                current = nextEdge ? nodeMap[nextEdge.target] : null;
            }
            return chain;
        };
        edges.forEach(edge => {
            const src = nodeMap[edge.source], tgt = nodeMap[edge.target];
            if (!src || !tgt) return;
            const waJson = getWaJson(tgt); if (!waJson) return;
            if (src.type === "wait_reply" && edge.kind === "rule" && edge.ruleIdx != null) {
                const rule = src.data?.rules?.[edge.ruleIdx]; if (!rule?.keyword) return;
                const keywords = rule.keyword.split(",").map(k => k.trim().toLowerCase()).filter(Boolean); if (!keywords.length) return;
                const key = `kw-${src.id}-${tgt.id}-${keywords.join("|")}`;
                if (!triggerSet.has(key)) { triggerSet.add(key); triggerMap.push({ triggerType: "keyword", triggers: keywords, label: rule.label || "Keyword match", sourceNodeId: src.id, replyNodeId: tgt.id, replyNodeType: tgt.type, waJsonChain: collectMessageChain(tgt) }); }
                return;
            }
            if (src.type === "wait_reply" && edge.kind === "fallback") {
                const key = `fb-${src.id}-${tgt.id}`;
                if (!triggerSet.has(key)) { triggerSet.add(key); triggerMap.push({ triggerType: "fallback", triggers: ["*"], label: "Fallback — anything else", sourceNodeId: src.id, replyNodeId: tgt.id, replyNodeType: tgt.type, waJsonChain: collectMessageChain(tgt) }); }
                return;
            }
            if (src.type === "interactive" && edge.kind === "btn" && edge.btnIdx != null) {
                const btn = src.data?.buttons?.[edge.btnIdx]; if (!btn?.id) return;
                const key = `btn-${btn.id}-${src.id}-${tgt.id}`;
                if (!triggerSet.has(key)) { triggerSet.add(key); triggerMap.push({ triggerType: "button_reply", triggers: [btn.id], triggerTitle: btn.title, label: `Button: "${btn.title}"`, sourceNodeId: src.id, replyNodeId: tgt.id, replyNodeType: tgt.type, waJsonChain: collectMessageChain(tgt) }); }
            }
        });
        return { builderFlow: { nodes, edges }, runTimeFlow: { triggerMap }, totalTriggers: triggerMap.length };
    }, [nodes, edges, buildNodeWaJson]);

    const preview = useMemo(() => {
        const msgs = []; const nodeMap = Object.fromEntries(nodes.map(n => [n.id, n]));
        let cur = nodes.find(n => n.type === "start"); const visited = new Set();
        while (cur && !visited.has(cur.id)) {
            visited.add(cur.id);
            if (cur.type === "text_msg" && cur.data.text) msgs.push({ role: "bot", text: cur.data.text });
            if (cur.type === "interactive") {
                msgs.push({ role: "interactive", data: cur.data, nodeId: cur.id });
                const btnEdge = edges.find(e => e.source === cur.id && e.kind === "btn");
                if (btnEdge) { msgs.push({ role: "user", text: cur.data?.buttons?.[btnEdge.btnIdx]?.title || "Button click" }); cur = nodeMap[btnEdge.target]; continue; }
                break;
            }
            if (cur.type === "delay") msgs.push({ role: "sys", text: `⏱ ${cur.data.seconds || 2}s delay` });
            if (cur.type === "end") { msgs.push({ role: "sys", text: "⏹ Conversation ended" }); break; }
            if (cur.type === "wait_reply") {
                msgs.push({ role: "sys", text: "⌨️ Waiting for reply…" });
                msgs.push({ role: "branches", rules: cur.data?.rules || [], nodeId: cur.id });
                const ruleEdge = edges.find(e => e.source === cur.id && e.kind === "rule");
                const fbEdge = edges.find(e => e.source === cur.id && e.kind === "fallback");
                if (ruleEdge) { const rule = cur.data?.rules?.[ruleEdge.ruleIdx]; msgs.push({ role: "user", text: rule?.keyword?.split(",")[0] || "user message", matched: rule?.label }); cur = nodeMap[ruleEdge.target]; continue; }
                if (fbEdge) { msgs.push({ role: "user", text: "random message" }); cur = nodeMap[fbEdge.target]; continue; }
                break;
            }
            const nextEdge = edges.find(e => e.source === cur.id && e.kind !== "btn" && e.kind !== "rule" && e.kind !== "fallback");
            if (!nextEdge) break; cur = nodeMap[nextEdge.target];
        }
        return msgs;
    }, [nodes, edges]);

    const reachableIds = useMemo(() => {
        const startNode = nodes.find(n => n.type === "start"); if (!startNode) return new Set();
        const visited = new Set(); const queue = [startNode.id];
        while (queue.length) { const cur = queue.shift(); if (visited.has(cur)) continue; visited.add(cur); edges.filter(e => e.source === cur).forEach(e => { if (!visited.has(e.target)) queue.push(e.target); }); }
        return visited;
    }, [nodes, edges]);

    const cvPos = useCallback(e => {
        const el = cvs.current; if (!el) return { x: 0, y: 0 };
        const r = el.getBoundingClientRect();
        return { x: e.clientX - r.left + el.scrollLeft, y: e.clientY - r.top + el.scrollTop };
    }, []);

    const onMouseMove = e => {
        const p = cvPos(e); setMouse(p);
        if (drag) setNodes(prev => prev.map(n => n.id === drag.id ? { ...n, x: p.x - drag.dx, y: p.y - drag.dy } : n));
    };
    const onMouseUp = e => {
        if (conn) {
            const p = cvPos(e);
            const tgt = nodes.find(n => { const ip = inPos(n); return Math.abs(p.x - ip.x) < 24 && Math.abs(p.y - ip.y) < 24 && n.id !== conn.src; });
            if (tgt) { const dup = edges.some(ed => ed.source === conn.src && ed.target === tgt.id && ed.kind === conn.kind && ed.btnIdx === conn.btnIdx && ed.ruleIdx === conn.ruleIdx); if (!dup) setEdges(p => [...p, { id: `e${Date.now()}`, source: conn.src, target: tgt.id, kind: conn.kind, btnIdx: conn.btnIdx, ruleIdx: conn.ruleIdx }]); }
            setConn(null);
        }
        setDrag(null);
    };

    const addNode = type => {
        const n = {
            id: `n${Date.now()}`, type, x: 200 + Math.random() * 80, y: 200 + Math.random() * 80,
            data: (type === "text_msg" || type === 'unsubscribe' || type === 'leads') ? { text: "" }
                : type === "interactive" ? { headerType: "none", header: "", headerLink: "", body: "", footer: "", buttons: [{ id: `b${Date.now()}`, title: "Yes", color: "#1D74F5" }] }
                    : type === "wait_reply" ? { rules: [{ keyword: "hi", label: "Greeting", color: RULE_COLORS[0] }] }
                        : type === "delay" ? { seconds: 3 } : {}
        };
        setNodes(p => [...p, n]); setSel(n.id);
    };

    const delNode = id => { setNodes(p => p.filter(n => n.id !== id)); setEdges(p => p.filter(e => e.source !== id && e.target !== id)); if (sel === id) setSel(null); };
    const upd = (id, data) => setNodes(p => p.map(n => n.id === id ? { ...n, data: { ...n.data, ...data } } : n));
    const addBtn = id => { const n = nodes.find(n => n.id === id); const b = [...(n?.data?.buttons || [])]; if (b.length >= 3) return; upd(id, { buttons: [...b, { id: `b${Date.now()}`, title: "New button", color: BTN_COLORS[b.length % BTN_COLORS.length] }] }); };
    const updBtn = (id, i, p) => { const n = nodes.find(n => n.id === id); const b = [...(n?.data?.buttons || [])]; b[i] = { ...b[i], ...p }; upd(id, { buttons: b }); };
    const delBtn = (id, i) => { const n = nodes.find(n => n.id === id); upd(id, { buttons: (n?.data?.buttons || []).filter((_, j) => j !== i) }); setEdges(p => p.filter(e => !(e.source === id && e.kind === "btn" && e.btnIdx === i)).map(e => e.source === id && e.kind === "btn" && e.btnIdx > i ? { ...e, btnIdx: e.btnIdx - 1 } : e)); };
    const addRule = id => { const n = nodes.find(n => n.id === id); const r = [...(n?.data?.rules || [])]; upd(id, { rules: [...r, { keyword: "", label: "New rule", color: RULE_COLORS[r.length % RULE_COLORS.length] }] }); };
    const updRule = (id, i, p) => { const n = nodes.find(n => n.id === id); const r = [...(n?.data?.rules || [])]; r[i] = { ...r[i], ...p }; upd(id, { rules: r }); };
    const delRule = (id, i) => { const n = nodes.find(n => n.id === id); upd(id, { rules: (n?.data?.rules || []).filter((_, j) => j !== i) }); setEdges(p => p.filter(e => !(e.source === id && e.kind === "rule" && e.ruleIdx === i)).map(e => e.source === id && e.kind === "rule" && e.ruleIdx > i ? { ...e, ruleIdx: e.ruleIdx - 1 } : e)); };
    const getEdgeTgt = (nodeId, kind, idx) => {
        const e = edges.find(ed => { if (ed.source !== nodeId || ed.kind !== kind) return false; if (kind === "btn") return ed.btnIdx === idx; if (kind === "rule") return ed.ruleIdx === idx; if (kind === "fallback") return true; return false; });
        if (!e) return null;
        const t = nodes.find(n => n.id === e.target);
        return t ? (t.data?.text?.slice(0, 28) || t.data?.body?.slice(0, 28) || CFG[t.type]?.label) : null;
    };

    const getInitials = (string) => {
        if (!!string && string.length > 0) {
            var names = string?.split(' '), initials = names[0]?.substring(0, 1)?.toUpperCase();
            if (names?.length > 1) initials += names[names?.length - 1]?.substring(0, 1)?.toUpperCase();
            else { names = string?.split('@'); initials = names[0]?.substring(0, 1)?.toUpperCase(); if (names?.length > 1) initials += names[names?.length - 1]?.substring(0, 1)?.toUpperCase(); }
            return initials;
        }
        return 'N/A';
    };
    const renderAvatar = (userName) => (
        <Avatar size={'small'} sx={{ bgcolor: '#108764 !important', width: '35px', height: '35px', fontSize: '14px' }}>{getInitials(userName)}</Avatar>
    );

    const treeLayout = useCallback(() => {
        if (!nodes.length) return;
        const H_GAP = 420, V_GAP = 260, START_X = 400, START_Y = 80;
        const startNode = nodes.find(n => n.type === "start"); if (!startNode) return;
        const childrenMap = {}; nodes.forEach(n => (childrenMap[n.id] = []));
        edges.forEach(e => { if (childrenMap[e.source] && !childrenMap[e.source].includes(e.target)) childrenMap[e.source].push(e.target); });
        const posMap = {}; let currentX = 0;
        const dfs = (nodeId, depth) => {
            const children = childrenMap[nodeId] || [];
            if (!children.length) { const x = currentX++; posMap[nodeId] = { x: START_X + x * H_GAP, y: START_Y + depth * V_GAP }; return x; }
            const childXs = children.map(childId => dfs(childId, depth + 1));
            const midX = (Math.min(...childXs) + Math.max(...childXs)) / 2;
            posMap[nodeId] = { x: START_X + midX * H_GAP, y: START_Y + depth * V_GAP }; return midX;
        };
        dfs(startNode.id, 0);
        nodes.filter(n => !posMap[n.id]).forEach((n, i) => { posMap[n.id] = { x: START_X + (currentX + i) * H_GAP, y: START_Y + 4 * V_GAP }; });
        setNodes(prev => prev.map(n => ({ ...n, x: posMap[n.id]?.x ?? n.x, y: posMap[n.id]?.y ?? n.y })));
    }, [nodes, edges]);

    const fitView = useCallback(() => {
        if (!nodes.length || !cvs.current) return;
        const PADDING = 60;
        const minX = Math.min(...nodes.map(n => n.x)) - PADDING, minY = Math.min(...nodes.map(n => n.y)) - PADDING;
        const maxX = Math.max(...nodes.map(n => n.x + NODE_W)) + PADDING, maxY = Math.max(...nodes.map(n => n.y + nodeH(n))) + PADDING;
        const newZoom = Math.min(cvs.current.clientWidth / (maxX - minX), cvs.current.clientHeight / (maxY - minY), 1.2);
        setZoom(newZoom);
        setPan({ x: (cvs.current.clientWidth - (maxX - minX) * newZoom) / 2 - minX * newZoom, y: (cvs.current.clientHeight - (maxY - minY) * newZoom) / 2 - minY * newZoom });
    }, [nodes]);

    const toggleExpand = () => handle.active ? handle.exit() : handle.enter();

    return (
        <FullScreenView handle={handle}>
            <div className="autoReplies-root" style={{ cursor: loading === 'wait' ? 'wait' : 'auto' }}>

                <div className="autoReplies-sidebar">
                    <div className="autoReplies-sidebar-brand">
                        <div className="autoReplies-sidebar-logo-row">
                            <div className="autoReplies-sidebar-logo">{renderAvatar(props?.whatsappFlow?.flowName)}</div>
                            <span className="autoReplies-sidebar-title">{props?.whatsappFlow?.flowName}</span>
                        </div>
                        <div className="autoReplies-sidebar-subtitle">Whatsapp Flow</div>
                    </div>
                    <div className="autoReplies-lbl">Add Flows</div>
                    {Object.entries(CFG).filter(([k]) => k !== "start").map(([type, cfg]) => (
                        type !== 'wait_reply' && (
                            <div key={type} className="autoReplies-pal-item" onClick={() => addNode(type)}>
                                <div className="autoReplies-pal-icon" style={{ background: cfg.light, border: `1.5px solid ${cfg.border}` }}>{cfg.icon}</div>
                                <div><div className="autoReplies-pal-label">{cfg.label}</div><div className="autoReplies-pal-desc">{cfg.desc}</div></div>
                            </div>
                        )
                    ))}
                    <div className="autoReplies-sidebar-footer">
                        <div className="autoReplies-sidebar-hint">
                            <div>🔵 Drag <b>●</b> handle to connect</div>
                            <div>🗑 Click edge to delete</div>
                            <div>⌨️ Keyword = user trigger</div>
                        </div>
                    </div>
                </div>

                <div className="autoReplies-main">
                    <div className="autoReplies-topbar">
                        <div className="autoReplies-tab-group">
                            {["flow", "preview"].map(t => (
                                <button key={t} className={`autoReplies-tab-btn${tab === t ? " active" : ""}`} onClick={() => setTab(t)}>
                                    {t === "flow" ? "⚡ Flow" : "💬 Preview"}
                                </button>
                            ))}
                        </div>
                        <div className="autoReplies-topbar-right">
                            <button className="autoReplies-zoom-btn autoReplies-zoom-fit" onClick={treeLayout} title="Auto-arrange" style={{ borderLeft: "1.5px solid #E2E8F0", marginLeft: 3, color: "#0A8F72" }}><Network size={16} /></button>
                            <button className="autoReplies-zoom-btn autoReplies-zoom-fit" title="Full screen" style={{ borderLeft: "1.5px solid #E2E8F0", marginLeft: 3, color: "#0A8F72" }} onClick={toggleExpand}>{handle.active ? <Minimize size={16} /> : <Fullscreen size={16} />}</button>
                            <span className="autoReplies-node-count">{nodes.length} nodes · {edges.length} edges</span>
                            <button className="autoReplies-btn-save" onClick={handleSaveButton}>💾 Save</button>
                            <button className="autoReplies-btn-close" onClick={props?.onClose}>X</button>
                        </div>
                    </div>

                    {tab === "flow" ? (
                        <div ref={cvs} className="autoReplies-canvas" style={{ cursor: drag ? "grabbing" : "default" }} onMouseMove={onMouseMove} onMouseUp={onMouseUp}>
                            <div className="autoReplies-canvas-inner" style={{ transform: `translate(${pan.x}px,${pan.y}px) scale(${zoom})`, transformOrigin: "0 0", transition: "transform 0.45s cubic-bezier(0.4,0,0.2,1)" }}>
                                <svg className="autoReplies-canvas-svg">
                                    <defs>
                                        <pattern id="grid" x="0" y="0" width="28" height="28" patternUnits="userSpaceOnUse">
                                            <circle cx="1" cy="1" r="1.1" fill="#C5D0DC" />
                                        </pattern>
                                    </defs>
                                    <rect width="100%" height="100%" fill="url(#grid)" />
                                    {edges.map(edge => {
                                        const s = nodes.find(n => n.id === edge.source), t = nodes.find(n => n.id === edge.target);
                                        if (!s || !t) return null;
                                        let o;
                                        if (s.type === "interactive" && edge.kind === "btn" && edge.btnIdx != null) o = btnHandlePos(s, edge.btnIdx);
                                        else if (s.type === "wait_reply" && edge.kind === "rule" && edge.ruleIdx != null) o = ruleHandlePos(s, edge.ruleIdx);
                                        else if (s.type === "wait_reply" && edge.kind === "fallback") o = fallbackHandlePos(s);
                                        else o = outPos(s);
                                        const ip = inPos(t), d = bez(o.x, o.y, ip.x, ip.y);
                                        const btn = s.type === "interactive" && edge.kind === "btn" ? s.data?.buttons?.[edge.btnIdx] : null;
                                        const rule = s.type === "wait_reply" && edge.kind === "rule" ? s.data?.rules?.[edge.ruleIdx] : null;
                                        const color = btn ? (btn.color || "#1D74F5") : rule ? (rule.color || "#7C3AED") : edge.kind === "fallback" ? "#94A3B8" : CFG[s.type]?.bg || "#25D366";
                                        return (
                                            <g key={edge.id} style={{ pointerEvents: "all" }}>
                                                <path d={d} stroke={color + "22"} strokeWidth="10" fill="none" strokeLinecap="round" opacity={reachableIds.has(edge.source) && reachableIds.has(edge.target) ? 1 : 0.2} />
                                                <path d={d} stroke={color} strokeWidth="2.2" fill="none" strokeLinecap="round" strokeDasharray="8 5" opacity={reachableIds.has(edge.source) && reachableIds.has(edge.target) ? 0.7 : 0.15} className="autoReplies-edge-anim" />
                                                <path d={d} stroke="transparent" strokeWidth="18" fill="none" strokeLinecap="round" style={{ cursor: "pointer", pointerEvents: "stroke" }} onClick={() => setEdges(p => p.filter(x => x.id !== edge.id))} />
                                                <circle cx={ip.x} cy={ip.y} r="5.5" fill={color} opacity=".85" />
                                            </g>
                                        );
                                    })}
                                    {conn && (() => {
                                        const s = nodes.find(n => n.id === conn.src); if (!s) return null;
                                        let o;
                                        if (s.type === "interactive" && conn.kind === "btn") o = btnHandlePos(s, conn.btnIdx);
                                        else if (s.type === "wait_reply" && conn.kind === "rule") o = ruleHandlePos(s, conn.ruleIdx);
                                        else if (s.type === "wait_reply" && conn.kind === "fallback") o = fallbackHandlePos(s);
                                        else o = outPos(s);
                                        const btn = s.type === "interactive" && conn.kind === "btn" ? s.data?.buttons?.[conn.btnIdx] : null;
                                        const rule = s.type === "wait_reply" && conn.kind === "rule" ? s.data?.rules?.[conn.ruleIdx] : null;
                                        const color = btn ? (btn.color || "#1D74F5") : rule ? (rule.color || "#7C3AED") : conn.kind === "fallback" ? "#94A3B8" : CFG[s.type]?.bg || "#25D366";
                                        return <path d={bez(o.x, o.y, mouse.x, mouse.y)} stroke={color} strokeWidth="2.5" fill="none" strokeLinecap="round" strokeDasharray="7 4" opacity=".55" />;
                                    })()}
                                </svg>

                                {nodes.map(node => {
                                    const cfg = CFG[node.type]; const isSel = sel === node.id;
                                    return (
                                        <div key={node.id} className="autoReplies-node-wrap"
                                            style={{ left: node.x, top: node.y, width: NODE_W, border: `1.5px solid ${isSel ? cfg.bg : cfg.border}`, boxShadow: isSel ? `0 0 0 3px ${cfg.bg}28, 0 8px 24px rgba(0,0,0,.12)` : "0 4px 14px rgba(0,0,0,.08)", opacity: reachableIds.has(node.id) ? 1 : 0.98, filter: reachableIds.has(node.id) ? "none" : "grayscale(99%)", transition: "opacity 0.3s, filter 0.3s" }}
                                            onMouseDown={e => { e.stopPropagation(); const p = cvPos(e); setDrag({ id: node.id, dx: p.x - node.x, dy: p.y - node.y }); setSel(node.id); }}>
                                            {!['wait_reply', 'start'].includes(node.type) && (whatsappFlowProtection ? node.id !== 'n3' : true) && (<div className="autoReplies-node-del" onClick={e => { e.stopPropagation(); delNode(node.id); }}>✕</div>)}
                                            {node.type !== "start" && <div className="autoReplies-handle-in" style={{ border: `2.5px solid ${cfg.border}` }} />}
                                            <div className="autoReplies-node-hdr" style={{ background: cfg.light, borderBottom: `1px solid ${cfg.border}` }}>
                                                <div className="autoReplies-node-hdr-icon" style={{ border: `1.5px solid ${cfg.border}` }}>{cfg.icon}</div>
                                                <span className="autoReplies-node-hdr-type" style={{ color: cfg.bg }}>{cfg.label.toUpperCase()}</span>
                                                {!reachableIds.has(node.id) && <Chip label="Disconnected" size='small' sx={{ marginLeft: 'auto' }} color="error" />}
                                                {isSel && <div className="autoReplies-node-hdr-dot" style={{ background: cfg.bg }} />}
                                            </div>
                                            <div className="autoReplies-node-body" style={{ padding: `${PAD}px 12px ${node.type === "interactive" || node.type === "wait_reply" ? PAD : 13}px` }}>
                                                {node.type === "start" && <div className="autoReplies-node-muted">Entry point — flow starts here</div>}
                                                {node.type === "end" && <div className="autoReplies-node-muted">Conversation ends here</div>}
                                                {node.type === "delay" && <div className="autoReplies-node-text-preview">⏱ {node.data.seconds || 2}s wait</div>}
                                                {(node.type === "text_msg" || node.type === 'unsubscribe' || node.type === 'leads') && <div className="autoReplies-node-text-preview">{node.data.text || <span className="autoReplies-node-text-empty">Empty message…</span>}</div>}

                                                {node.type === "interactive" && (() => {
                                                    const hdrType = node.data.headerType;
                                                    const hasHdr = hdrType && hdrType !== "none";
                                                    const isImg = hdrType === "image";
                                                    const isVid = hdrType === "video";
                                                    const isDoc = hdrType === "document";
                                                    return (<>
                                                        {hasHdr && (
                                                            isImg && node.data.headerLink ? (
                                                                <img src={node.data.headerLink} alt="header" style={{ width: '100%', height: 72, objectFit: 'cover', borderRadius: 6, marginBottom: 6, display: 'block', border: '1px solid #E4E7EC' }} onError={e => { e.target.style.display = 'none'; }} />
                                                            ) : isImg ? (
                                                                <div style={{ width: '100%', height: 32, background: '#EBF3FF', borderRadius: 6, marginBottom: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#1D74F5' }}>🖼 Image URL not set</div>
                                                            ) : isVid ? (
                                                                <div style={{ width: '100%', height: 32, background: '#F3EDFD', borderRadius: 6, marginBottom: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#7C3AED', fontWeight: 500 }}>
                                                                    🎥 {node.data.headerLink ? urlFilename(node.data.headerLink, 'video') : 'Video URL not set'}
                                                                </div>
                                                            ) : isDoc ? (
                                                                <div style={{ width: '100%', height: 32, background: '#FFF7ED', borderRadius: 6, marginBottom: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#D97706', fontWeight: 500 }}>
                                                                    📄 {node.data.headerLink ? urlFilename(node.data.headerLink, 'document') : 'Document URL not set'}
                                                                </div>
                                                            ) : (
                                                                <div className="autoReplies-node-int-hdr">{node.data.header || "Header"}</div>
                                                            )
                                                        )}
                                                        <div className="autoReplies-node-int-body">{node.data.body || <span className="autoReplies-node-text-empty">Body text…</span>}</div>
                                                        {!!node.data.footer && <div className="autoReplies-node-int-footer">{node.data.footer}</div>}
                                                        <div className="autoReplies-node-divider" />
                                                        {(node.data.buttons || []).map((btn, i) => {
                                                            const isConn = edges.some(e => e.source === node.id && e.kind === "btn" && e.btnIdx === i);
                                                            return (
                                                                <div key={i} className="autoReplies-btn-row">
                                                                    <div className="autoReplies-handle-out" style={{ background: (btn.color || "#1D74F5"), boxShadow: `0 1px 5px ${btn.color || "#1D74F5"}50` }} onMouseDown={e => { e.stopPropagation(); setConn({ src: node.id, kind: "btn", btnIdx: i }); }} />
                                                                    <div className="autoReplies-chip" style={{ background: (btn.color || "#1D74F5") + "14", border: `1.5px solid ${btn.color || "#1D74F5"}30`, color: btn.color || "#1D74F5" }}>{btn.title || "Button"}</div>
                                                                    <div className="autoReplies-handle-out" style={{ background: (btn.color || "#1D74F5"), boxShadow: `0 1px 5px ${btn.color || "#1D74F5"}50` }} onMouseDown={e => { e.stopPropagation(); setConn({ src: node.id, kind: "btn", btnIdx: i }); }} />
                                                                </div>
                                                            );
                                                        })}
                                                    </>);
                                                })()}

                                                {node.type === "wait_reply" && (<>
                                                    <div className="autoReplies-wr-prompt"><span>⌨️</span> User types a message…</div>
                                                    {(node.data?.rules || []).map((rule, i) => {
                                                        const isConn = edges.some(e => e.source === node.id && e.kind === "rule" && e.ruleIdx === i);
                                                        return (
                                                            <div key={i} className="autoReplies-rule-row">
                                                                <div className="autoReplies-handle-out" style={{ background: (rule.color || "#7C3AED"), boxShadow: `0 1px 5px ${rule.color || "#7C3AED"}50` }} onMouseDown={e => { e.stopPropagation(); setConn({ src: node.id, kind: "rule", ruleIdx: i }); }} />
                                                                <div className="autoReplies-chip" style={{ background: (rule.color || "#7C3AED") + "14", border: `1.5px solid ${rule.color || "#7C3AED"}30`, color: rule.color || "#7C3AED", fontSize: 10.5 }}>
                                                                    {rule.keyword ? `"${rule.keyword.split(",")[0].trim()}"${rule.keyword.split(",").length > 1 ? ` +${rule.keyword.split(",").length - 1}` : ""}` : "(empty)"}
                                                                </div>
                                                                <div className="autoReplies-handle-out" style={{ background: (rule.color || "#7C3AED"), boxShadow: `0 1px 5px ${rule.color || "#7C3AED"}50` }} onMouseDown={e => { e.stopPropagation(); setConn({ src: node.id, kind: "rule", ruleIdx: i }); }} />
                                                            </div>
                                                        );
                                                    })}
                                                    <div className="autoReplies-fb-row">
                                                        <div className="autoReplies-handle-out" style={{ background: "#94A3B8" }} onMouseDown={e => { e.stopPropagation(); setConn({ src: node.id, kind: "fallback" }); }} />
                                                        <div className="autoReplies-chip autoReplies-chip-fallback">anything else…</div>
                                                        <div className="autoReplies-handle-out" style={{ background: "#94A3B8" }} onMouseDown={e => { e.stopPropagation(); setConn({ src: node.id, kind: "fallback" }); }} />
                                                    </div>
                                                </>)}
                                            </div>
                                            {node.type !== "end" && node.type !== "interactive" && node.type !== "wait_reply" && (
                                                <div className="autoReplies-handle-bottom" style={{ background: cfg.bg, boxShadow: `0 2px 6px ${cfg.bg}50` }} onMouseDown={e => { e.stopPropagation(); setConn({ src: node.id, kind: "out" }); }} />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : (
                        <div className="autoReplies-preview-wrap">
                            <div className="autoReplies-phone"><WhatsappBotPreview payload={saasFlowPayload} /></div>
                        </div>
                    )}
                </div>

                {selNode && tab === "flow" && (
                    <div className="autoReplies-panel">
                        <div className="autoReplies-panel-hdr">
                            <div>
                                <div className="autoReplies-panel-type" style={{ color: CFG[selNode.type].bg }}>{CFG[selNode.type].label.toUpperCase()}</div>
                                <div className="autoReplies-panel-id">id: {selNode.id}</div>
                            </div>
                            {!['wait_reply', 'start'].includes(selNode.type) && (whatsappFlowProtection ? selNode.id !== 'n3' : true) && (
                                <button className="autoReplies-btn-delete" onClick={() => delNode(selNode.id)}>Delete</button>
                            )}
                        </div>
                        {(selNode.type === "text_msg" || selNode.type === 'unsubscribe' || selNode.type === 'leads') && (<>
                            <div className="autoReplies-lbl">MESSAGE BODY</div>
                            <div className="autoReplies-fmt-toolbar">
                                <button className="autoReplies-fmt-btn" title="Bold (*text*)" onClick={() => insertTextMsgFormat('*')}>           <b>B</b>                              </button>
                                <button className="autoReplies-fmt-btn" title="Italic (_text_)" onClick={() => insertTextMsgFormat('_')}>           <i>I</i>                              </button>
                                <button className="autoReplies-fmt-btn autoReplies-fmt-btn--strike" title="Strike (~text~)" onClick={() => insertTextMsgFormat('~')}>  S                    </button>
                                <button className="autoReplies-fmt-btn" title="Mono (```text```)" onClick={() => insertTextMsgFormat('```')}>         <code style={{ fontSize: 10 }}>{ }</code></button>
                                <div className="autoReplies-fmt-divider" />
                                <button className="autoReplies-fmt-btn" title="Emoji" onClick={() => setShowTextMsgEmoji(v => !v)}>       😊                                    </button>
                                <span className="autoReplies-char-count">{(selNode.data.text || '').length}/4096</span>
                            </div>
                            {showTextMsgEmoji && (
                                <div style={{ position: 'relative', zIndex: 99 }}>
                                    <EmojiPicker onEmojiClick={handleTextMsgEmojiClick} searchDisabled height={300} width="100%" />
                                </div>
                            )}
                            <textarea
                                ref={textMsgRef}
                                className="autoReplies-ifield autoReplies-ifield--body"
                                style={{ minHeight: 100 }}
                                rows={5}
                                value={selNode.data.text || ""}
                                onChange={e => upd(selNode.id, { text: DOMPurify.sanitize(e.target.value) })}
                                placeholder={"Type your message…\n\n*bold* · _italic_ · ~strike~\n{{1}} {{2}} for variables"}
                            />
                            <div className="autoReplies-fmt-hint">*bold* · _italic_ · ~strike~ · ```mono``` · {"{{1}}"} variables</div>
                        </>)}

                        {selNode.type === "interactive" && (<>
                            <div className="autoReplies-info-box autoReplies-info-box-blue">
                                <b>WA Interactive Message</b><br />Up to 3 tappable reply buttons. Each routes independently.
                            </div>

                            <div className="autoReplies-lbl">
                                HEADER <span style={{ color: "#B0BCCA", fontWeight: 400 }}>(optional)</span>
                            </div>
                            <div className="autoReplies-seg" style={{ flexWrap: 'wrap', gap: 4 }}>
                                {['none', 'text', 'image', 'video', 'document'].map(t => (
                                    <button key={t} className={`autoReplies-seg-btn${selNode.data.headerType === t ? " active" : ""}`}
                                        onClick={() => upd(selNode.id, { headerType: t, header: '', headerLink: '' })}>
                                        {t.charAt(0).toUpperCase() + t.slice(1)}
                                    </button>
                                ))}
                            </div>

                            {selNode.data.headerType === 'text' && (
                                <input className="autoReplies-ifield" maxLength={60}
                                    value={selNode.data.header || ''}
                                    onChange={e => upd(selNode.id, { header: DOMPurify.sanitize(e.target.value) })}
                                    placeholder="Header text… (max 60 chars)" />
                            )}

                            {['image', 'video', 'document'].includes(selNode.data.headerType) && (<>
                                <input className="autoReplies-ifield"
                                    value={selNode.data.headerLink || ''}
                                    onChange={e => upd(selNode.id, { headerLink: DOMPurify.sanitize(e.target.value) })}
                                    placeholder={HEADER_PLACEHOLDERS[selNode.data.headerType] || 'https://…'} />
                                {selNode.data.headerType === 'image' && selNode.data.headerLink && (
                                    <img src={selNode.data.headerLink} alt="preview"
                                        style={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 6, marginBottom: 6, border: '1px solid #E4E7EC' }}
                                        onError={e => { e.target.style.display = 'none'; }} />
                                )}
                                {selNode.data.headerType === 'video' && selNode.data.headerLink && (
                                    <div style={{ padding: '6px 10px', background: '#F3EDFD', borderRadius: 6, marginBottom: 6, fontSize: 11, color: '#7C3AED', fontWeight: 500 }}>
                                        🎥 {urlFilename(selNode.data.headerLink, 'video')}
                                    </div>
                                )}
                                {selNode.data.headerType === 'document' && selNode.data.headerLink && (
                                    <div style={{ padding: '6px 10px', background: '#FFF7ED', borderRadius: 6, marginBottom: 6, fontSize: 11, color: '#D97706', fontWeight: 500 }}>
                                        📄 {urlFilename(selNode.data.headerLink, 'document')}
                                    </div>
                                )}
                            </>)}

                            <div className="autoReplies-lbl" style={{ marginTop: 10 }}>
                                BODY <span style={{ color: "#DC2626" }}>*</span>
                            </div>

                            <div className="autoReplies-fmt-toolbar">
                                <button className="autoReplies-fmt-btn" title="Bold (*text*)" onClick={() => insertBodyFormat('*')}>           <b>B</b>                         </button>
                                <button className="autoReplies-fmt-btn" title="Italic (_text_)" onClick={() => insertBodyFormat('_')}>           <i>I</i>                         </button>
                                <button className="autoReplies-fmt-btn autoReplies-fmt-btn--strike" title="Strike (~text~)" onClick={() => insertBodyFormat('~')}>  S                               </button>
                                <button className="autoReplies-fmt-btn" title="Mono (```text```)" onClick={() => insertBodyFormat('```')}>         <code style={{ fontSize: 10 }}>{ }</code></button>
                                <div className="autoReplies-fmt-divider" />
                                <button className="autoReplies-fmt-btn" title="Emoji" onClick={() => setShowBodyEmoji(v => !v)}>       😊                               </button>
                                <span className="autoReplies-char-count">{(selNode.data.body || '').length}/1024</span>
                            </div>

                            {showBodyEmoji && (
                                <div style={{ position: 'relative', zIndex: 99 }}>
                                    <EmojiPicker onEmojiClick={handleBodyEmojiClick} searchDisabled height={300} width="100%" />
                                </div>
                            )}

                            <textarea
                                ref={bodyPanelRef}
                                className="autoReplies-ifield autoReplies-ifield--body"
                                rows={5}
                                maxLength={1024}
                                value={selNode.data.body || ""}
                                onChange={e => upd(selNode.id, { body: DOMPurify.sanitize(e.target.value) })}
                                placeholder={"Main message body…\n\n*bold* · _italic_ · ~strike~\n{{1}} {{2}} for variables"}
                            />
                            <div className="autoReplies-fmt-hint">
                                *bold* · _italic_ · ~strike~ · ```mono``` · {"{{1}}"} variables
                            </div>

                            <div className="autoReplies-lbl" style={{ marginTop: 10 }}>
                                FOOTER <span style={{ color: "#B0BCCA", fontWeight: 400 }}>(optional)</span>
                            </div>
                            <input className="autoReplies-ifield" maxLength={60}
                                value={selNode.data.footer || ""}
                                onChange={e => upd(selNode.id, { footer: DOMPurify.sanitize(e.target.value) })}
                                placeholder="Small text below… (max 60 chars)" />

                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, marginTop: 10 }}>
                                <div className="autoReplies-lbl" style={{ marginBottom: 0 }}>
                                    REPLY BUTTONS <span style={{ color: "#B0BCCA", fontWeight: 400 }}>max 3</span>
                                </div>
                                {(selNode.data.buttons || []).length < 3 && (
                                    <button className="autoReplies-btn-add" onClick={() => addBtn(selNode.id)}>+ Add</button>
                                )}
                            </div>
                            {(selNode.data.buttons || []).map((btn, i) => {
                                const tgt = getEdgeTgt(selNode.id, "btn", i);
                                return (
                                    <div key={i} className="autoReplies-btn-card">
                                        <div className="autoReplies-btn-card-row">
                                            <span className="autoReplies-btn-card-idx">#{i + 1}</span>
                                            <input className="autoReplies-ifield" style={{ flex: 1, marginBottom: 0, padding: "6px 9px" }}
                                                value={btn.title}
                                                onChange={e => updBtn(selNode.id, i, { title: DOMPurify.sanitize(e.target.value) })}
                                                maxLength={20} placeholder="Button label" />
                                            <button className="autoReplies-btn-remove" onClick={() => delBtn(selNode.id, i)}>✕</button>
                                        </div>
                                        <div className="autoReplies-color-row">
                                            <span className="autoReplies-color-lbl">Color:</span>
                                            {BTN_COLORS.map(c => (
                                                <div key={c} className={`autoReplies-color-dot${btn.color === c ? " active" : ""}`} style={{ background: c }} onClick={() => updBtn(selNode.id, i, { color: c })} />
                                            ))}
                                        </div>
                                        {tgt
                                            ? <div className="autoReplies-edge-tgt">→ {tgt}…</div>
                                            : <div className="autoReplies-edge-empty">○ Drag the <span style={{ color: btn.color || "#1D74F5", fontWeight: 700 }}>●</span> handle to connect</div>
                                        }
                                    </div>
                                );
                            })}
                        </>)}

                        {selNode.type === "wait_reply" && (<>
                            <div className="autoReplies-info-box autoReplies-info-box-purple">
                                <b>Keyword Router</b><br />First match wins. Keywords are comma-separated, case-insensitive.
                            </div>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                                <div className="autoReplies-lbl" style={{ marginBottom: 0 }}>KEYWORD RULES</div>
                                {(selNode.data?.rules || []).length < 4 && <button className="autoReplies-rule-add" onClick={() => addRule(selNode.id)}>+ Add</button>}
                            </div>
                            {(selNode.data?.rules || []).map((rule, i) => {
                                const tgt = getEdgeTgt(selNode.id, "rule", i);
                                const keywords = rule.keyword ? rule.keyword.split(",").map(k => k.trim()).filter(Boolean) : [];
                                return (
                                    <div key={i} className="autoReplies-btn-card">
                                        <div className="autoReplies-btn-card-row">
                                            <span className="autoReplies-btn-card-idx">#{i + 1}</span>
                                            <input
                                                className="autoReplies-ifield"
                                                style={{ flex: 1, marginBottom: 0, padding: "6px 9px" }}
                                                value={rule.label || ""}
                                                onChange={e => updRule(selNode.id, i, { label: DOMPurify.sanitize(e.target.value) })}
                                                placeholder="Rule label (e.g. Greeting)"
                                            />
                                            <button
                                                className="autoReplies-kw-edit-btn"
                                                title="Edit keywords"
                                                onClick={() => {
                                                    setDraftKeywords(keywords.length ? [...keywords] : ['']);
                                                    setKeywordModal({ nodeId: selNode.id, ruleIdx: i });
                                                }}
                                            >
                                                <FileText size={14} />
                                            </button>
                                            {rule.label !== 'Unsubscribe' ?
                                                <button className="autoReplies-btn-remove" onClick={() => delRule(selNode.id, i)}>✕</button>
                                            : !whatsappFlowProtection &&
                                                <button className="autoReplies-btn-remove" onClick={() => delRule(selNode.id, i)}>✕</button>
                                            }
                                        </div>
                                        {keywords.length > 0 ? (
                                            <div className="autoReplies-kw-chips">
                                                {keywords.slice(0, 5).map((kw, ki) => (
                                                    <span key={ki} className="autoReplies-kw-chip" style={{ borderColor: rule.color || "#7C3AED", color: rule.color || "#7C3AED" }}>
                                                        {kw}
                                                    </span>
                                                ))}
                                                {keywords.length > 5 && (
                                                    <span className="autoReplies-kw-chip autoReplies-kw-chip--more" style={{ borderColor: rule.color || "#7C3AED", color: rule.color || "#7C3AED" }}>
                                                        +{keywords.length - 5} more
                                                    </span>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="autoReplies-kw-empty">No keywords — click <FileText size={11} style={{ verticalAlign: 'middle' }} /> to add</div>
                                        )}
                                        <div className="autoReplies-color-row">
                                            <span className="autoReplies-color-lbl">Color:</span>
                                            {RULE_COLORS.slice(0, 6).map(c => (
                                                <div key={c} className={`autoReplies-color-dot${rule.color === c ? " active" : ""}`} style={{ background: c }} onClick={() => updRule(selNode.id, i, { color: c })} />
                                            ))}
                                        </div>

                                        {tgt ? <div className="autoReplies-edge-tgt">→ {tgt}…</div> : <div className="autoReplies-edge-empty">○ Drag the <span style={{ color: rule.color || "#7C3AED", fontWeight: 700 }}>●</span> handle to connect</div>}
                                    </div>
                                );
                            })}
                            <div className="autoReplies-fallback-box">
                                <div className="autoReplies-fallback-title">🔀 Fallback — "anything else"</div>
                                {(() => { const tgt = getEdgeTgt(selNode.id, "fallback", undefined); return tgt ? <div className="autoReplies-edge-tgt">→ {tgt}…</div> : <div className="autoReplies-edge-empty">○ Drag the grey ● handle to connect</div>; })()}
                            </div>
                        </>)}
                        {selNode.type === "delay" && (<>
                            <div className="autoReplies-lbl">DELAY (SECONDS)</div>
                            <input type="number" min={1} max={60} className="autoReplies-ifield" value={selNode.data.seconds || 2} onChange={e => upd(selNode.id, { seconds: parseInt(e.target.value) || 2 })} />
                        </>)}

                        {(selNode.type === "start" || selNode.type === "end") && (
                            <div className="autoReplies-static-hint">
                                {selNode.type === "start" ? "Entry point. Connect to the first node below." : "Conversation ends here."}
                            </div>
                        )}
                    </div>
                )}
            </div>
            {keywordModal && (() => {
                const rule = nodes.find(n => n.id === keywordModal.nodeId)?.data?.rules?.[keywordModal.ruleIdx];
                const color = rule?.color || "#7C3AED";

                const closeModal = () => { setKeywordModal(null); setDraftKeywords([]); };

                const submitModal = () => {
                    const cleaned = draftKeywords.map(k => k.trim()).filter(Boolean);
                    updRule(keywordModal.nodeId, keywordModal.ruleIdx, { keyword: cleaned.join(",") });
                    closeModal();
                };
                const addRow = () => setDraftKeywords(prev => [...prev, '']);
                const removeRow = (idx) => setDraftKeywords(prev => prev.filter((_, i) => i !== idx));
                const editRow = (idx, val) => setDraftKeywords(prev => prev.map((k, i) => i === idx ? val : k));

                return (
                    <div className="autoReplies-modal-backdrop" onClick={closeModal}>
                        <div className="autoReplies-modal" onClick={e => e.stopPropagation()}>
                            <div className="autoReplies-modal-hdr" style={{ borderBottom: `3px solid ${color}` }}>
                                <div>
                                    <div className="autoReplies-modal-title">Keywords</div>
                                    <div className="autoReplies-modal-subtitle">
                                        {rule?.label || `Rule #${keywordModal.ruleIdx + 1}`} · case-insensitive, first match wins
                                    </div>
                                </div>
                                <button className="autoReplies-modal-close" onClick={closeModal}>✕</button>
                            </div>

                            <div className="autoReplies-modal-body">
                                {draftKeywords.length === 0 && (
                                    <div className="autoReplies-modal-empty">No keywords yet — click <b>+ Add Keyword</b> below</div>
                                )}
                                {draftKeywords.map((kw, idx) => (
                                    <div key={idx} className="autoReplies-modal-kw-row">
                                        <span className="autoReplies-modal-kw-num">{idx + 1}</span>
                                        <input
                                            className="autoReplies-ifield"
                                            style={{ flex: 1, marginBottom: 0, padding: "7px 10px" }}
                                            value={kw}
                                            autoFocus={idx === draftKeywords.length - 1}
                                            onChange={e => editRow(idx, DOMPurify.sanitize(e.target.value))}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter') { e.preventDefault(); addRow(); }
                                                if (e.key === 'Backspace' && kw === '' && draftKeywords.length > 1) { e.preventDefault(); removeRow(idx); }
                                            }}
                                            placeholder={`e.g. ${['hello', 'hi there', 'hey', 'good morning', 'start'][idx % 5]}`}
                                        />
                                        {draftKeywords.length > 1 && (
                                            <button className="autoReplies-btn-remove" onClick={() => removeRow(idx)}>✕</button>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <div className="autoReplies-modal-footer">
                                <button className="autoReplies-modal-add-btn" onClick={addRow}>+ Add Keyword</button>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <button className="autoReplies-modal-cancel" onClick={closeModal}>Cancel</button>
                                    <button
                                        className="autoReplies-modal-submit"
                                        style={{ background: color }}
                                        onClick={submitModal}
                                        disabled={draftKeywords.every(k => !k.trim())}>
                                        Save Keywords
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </FullScreenView>
    );
}